'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { Card, CardContent } from '@/components/ui/card';
import { UserCheck, Wrench, Zap, Heart, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';

gsap.registerPlugin(ScrollTrigger);

const benefits = [
    {
        icon: UserCheck,
        titleKey: 'whyChoose.benefits.expertDentists.title',
        descriptionKey: 'whyChoose.benefits.expertDentists.description',
        image: '/images/treatments/hollywood-smile/expert-dentists.webp'
    },
    {
        icon: Heart,
        titleKey: 'whyChoose.benefits.personalizedPlans.title',
        descriptionKey: 'whyChoose.benefits.personalizedPlans.description',
        image: '/images/treatments/hollywood-smile/personalized-plans.webp'
    },
    {
        icon: Zap,
        titleKey: 'whyChoose.benefits.stateOfArt.title',
        descriptionKey: 'whyChoose.benefits.stateOfArt.description',
        image: '/images/treatments/hollywood-smile/technology.webp'
    },
    {
        icon: Wrench,
        titleKey: 'whyChoose.benefits.patientCentric.title',
        descriptionKey: 'whyChoose.benefits.patientCentric.description',
        image: '/images/treatments/hollywood-smile/patient-experience.webp'
    },
    {
        icon: Clock,
        titleKey: 'whyChoose.benefits.lastingResults.title',
        descriptionKey: 'whyChoose.benefits.lastingResults.description',
        image: '/images/treatments/hollywood-smile/lasting-results.webp'
    }
];

export default function WhyChooseDentaWistaSection() {
    const { t } = useTranslation('hollywood-smile');
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

            // Cards stagger animation
            if (cardsRef.current?.children) {
                gsap.fromTo(cardsRef.current.children,
                    { opacity: 0, y: 60, scale: 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 80%",
                            end: "bottom 20%",
                            once: true
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="why-choose"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 right-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-10 left-20 w-48 h-48 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                        style={{ lineHeight: '1.6' }}
                    >
                        {t('whyChoose.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('whyChoose.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>{' '}
                        {t('whyChoose.title.part3')}
                    </h2>
                </div>

                {/* Benefits Grid */}
                <div ref={cardsRef} className="grid lg:grid-cols-2 gap-8">
                    {benefits.map((benefit, index) => (
                        <motion.div
                            key={index}
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Card className="h-full bg-slate-50 dark:bg-navy-950 border-slate-200 dark:border-slate-700 hover:border-gold/50 transition-all duration-300 group overflow-hidden">
                                <CardContent className="p-8">
                                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                                        {/* Icon and Text Content */}
                                        <div className="flex-1 space-y-4">
                                            {/* Icon */}
                                            <div className="w-16 h-16 bg-gold/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border-2 border-gold/30">
                                                <benefit.icon className="w-8 h-8 text-gold" />
                                            </div>

                                            {/* Content */}
                                            <div>
                                                <h3 className="text-2xl font-bold text-navy dark:text-white mb-4 group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300">
                                                    {t(benefit.titleKey)}
                                                </h3>
                                                <p className="text-slate-600 dark:text-slate-300 leading-loose text-lg">
                                                    {t(benefit.descriptionKey)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Image */}
                                        <div className="w-full lg:w-48 h-48 lg:h-32 rounded-xl overflow-hidden group-hover:scale-105 transition-transform duration-300 shadow-lg">
                                            <OptimizedImage
                                                src={benefit.image}
                                                alt={t(benefit.titleKey)}
                                                width={200}
                                                height={150}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>

                                    {/* Decorative bottom border */}
                                    <div className="mt-6 h-1 bg-gradient-to-r from-gold/20 via-gold/40 to-gold/20 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                >
                    <div className="bg-gold/10 rounded-2xl p-12 border border-gold/20 max-w-4xl mx-auto">
                        <div className="grid md:grid-cols-3 gap-8 items-center">
                            {/* Stats */}
                            <div className="text-center">
                                <div className="text-4xl font-bold text-gold mb-2">{t('whyChoose.stats.yearsExperience')}</div>
                                <div className="text-slate-600 dark:text-slate-300 font-medium">{t('whyChoose.stats.yearsExperienceLabel')}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl font-bold text-gold mb-2">{t('whyChoose.stats.happyPatients')}</div>
                                <div className="text-slate-600 dark:text-slate-300 font-medium">{t('whyChoose.stats.happyPatientsLabel')}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl font-bold text-gold mb-2">{t('whyChoose.stats.satisfactionRate')}</div>
                                <div className="text-slate-600 dark:text-slate-300 font-medium">{t('whyChoose.stats.satisfactionRateLabel')}</div>
                            </div>
                        </div>

                        <div className="mt-8 pt-8 border-t border-gold/20">
                            <h3 className="text-2xl font-bold text-navy dark:text-white mb-4">
                                {t('whyChoose.bottomSection.title')}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-300 text-lg max-w-2xl mx-auto">
                                {t('whyChoose.bottomSection.description')}
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
